/** How far a filing's due date is from today, said one way everywhere (U3, design uplift 2026-11-03).
 *
 * Home's "Next filing due" tile said **Sep 30, 2026** on 2 November as the next filing due, with no
 * word that it was 33 days gone, while the Executive dashboard said "33 days past due" for the same
 * filing. One vocabulary: a past date is **overdue** (`33 days overdue`), a coming one is **in 8 days**,
 * today is **due today**. Pure, so it is tested without a DOM.
 *
 * **The dates are the server's; the arithmetic is the number of whole days.** `days` is always a
 * count of days between two dates the server or the clock gave (`FilingRow.days_left` from the
 * summary, or `daysBetween(today, due_date)`); nothing here knows a deadline. A regime that is not law
 * yet (`in_force_caveat`) is never counted down to, so the caller passes `days: undefined` for it.
 */

export type DueKind = "overdue" | "today" | "ahead" | "none";

export interface DueStatus {
  kind: DueKind;
  /** Whole days; negative once passed. `undefined` for "none". */
  days: number | undefined;
  /** "33 days overdue": the chip, only for an overdue filing. */
  overdue: string | undefined;
  /** "in 8 days", "due today": the quiet words beside a date that is not late. */
  ahead: string | undefined;
}

function days(n: number): string {
  return `${String(n)} ${n === 1 ? "day" : "days"}`;
}

export function dueStatus(daysLeft: number | null | undefined): DueStatus {
  if (daysLeft === null || daysLeft === undefined) {
    return { kind: "none", days: undefined, overdue: undefined, ahead: undefined };
  }
  if (daysLeft < 0) {
    return { kind: "overdue", days: daysLeft, overdue: `${days(-daysLeft)} overdue`, ahead: undefined };
  }
  if (daysLeft === 0) {
    return { kind: "today", days: 0, overdue: undefined, ahead: "due today" };
  }
  return { kind: "ahead", days: daysLeft, overdue: undefined, ahead: `in ${days(daysLeft)}` };
}

/** "1 filing overdue", "3 filings overdue". */
export function overdueCount(n: number): string {
  return `${String(n)} ${n === 1 ? "filing" : "filings"} overdue`;
}
